/* ScientificSourcesPopover — generic peer-reviewed citation panel.
 *
 *  Trust signal за EU AI Act Art. 50. Може да се mount-не във всеки модул,
 *  който обещава clinical-grade insights. Citations source-of-truth: всеки
 *  модул дефинира своя `sources` array с verified PMID/DOI links.
 *
 *  Source format:
 *    {
 *      key: 'unique-id',
 *      title_bg / title_en: string,
 *      citation: 'Author et al. Year. Journal vol(issue):pp-pp.',
 *      role_bg / role_en: 'short role description in this module',
 *      url: 'https://doi.org/... | https://pubmed.ncbi.nlm.nih.gov/...',
 *    }
 */
import React, { useState } from "react";
import { BookOpen, X, ExternalLink } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

export default function ScientificSourcesPopover({ sources, label, testIdPrefix = "sources" }) {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const bg = language === "bg";

  if (!Array.isArray(sources) || sources.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-trigger`}
        className={`inline-flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-all border ${
          isDark
            ? "bg-[hsl(223,47%,14%)] text-[#10B981] border-[hsl(223,47%,22%)] hover:bg-[hsl(223,47%,18%)]"
            : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"
        }`}
      >
        <BookOpen className="w-3.5 h-3.5" />
        {label || (bg ? "Научни източници" : "Scientific sources")}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/50 p-4"
          onClick={() => setOpen(false)}
          data-testid={`${testIdPrefix}-modal`}
        >
          <div
            className={`w-full sm:max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl shadow-2xl ${
              isDark ? "bg-[hsl(223,47%,10%)] border border-[hsl(223,47%,20%)]" : "bg-white"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-start justify-between gap-3 p-4 sm:p-5 border-b sticky top-0 ${
              isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)]" : "bg-white border-gray-100"
            }`}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                    {label || (bg ? "Научни източници" : "Scientific sources")}
                  </h3>
                  <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {bg
                      ? "Изчисленията се базират на peer-reviewed данни — не AI прогноза."
                      : "Calculations are based on peer-reviewed data — not an AI guess."}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className={`p-1.5 rounded-lg transition-colors ${
                  isDark ? "hover:bg-[hsl(223,47%,18%)] text-gray-400" : "hover:bg-slate-100 text-gray-500"
                }`}
                aria-label={bg ? "Затвори" : "Close"}
                data-testid={`${testIdPrefix}-close`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-4 sm:p-5 space-y-3">
              {sources.map((s) => (
                <div
                  key={s.key}
                  className={`rounded-xl p-3 border ${
                    isDark
                      ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]"
                      : "bg-slate-50 border-gray-100"
                  }`}
                  data-testid={`${testIdPrefix}-source-${s.key}`}
                >
                  <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                    {bg ? s.title_bg : s.title_en}
                  </p>
                  <p className={`text-[11px] mt-1 italic ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {s.citation}
                  </p>
                  <p className={`text-[12px] mt-1.5 leading-relaxed ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    {bg ? s.role_bg : s.role_en}
                  </p>
                  {s.url && (
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mt-2 inline-flex items-center gap-1 text-[11px] font-semibold hover:underline ${
                        isDark ? "text-emerald-400" : "text-emerald-700"
                      }`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      {bg ? "Към публикацията" : "View publication"}
                    </a>
                  )}
                </div>
              ))}
            </div>

            <div className={`p-4 sm:p-5 border-t text-[11px] leading-relaxed ${
              isDark ? "border-[hsl(223,47%,20%)] text-gray-400" : "border-gray-100 text-gray-500"
            }`}>
              {bg
                ? "Образователен инструмент с изкуствен интелект · Не замества медицинска консултация · Съответствие с Регламента за ИИ на ЕС (ограничен риск, чл. 50)"
                : "Educational AI tool · Does not replace medical consultation · EU AI Act compliant (limited risk, Art. 50)"}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
